import type { EBroadcastStatus } from "@api/broadcast";

export type TStreamStatus = "online" | "offline";

export type TBroadcastStreamStatusChanged = {
	"2dStreamStatus": TStreamStatus;
	"3dStreamStatus": TStreamStatus;
	hasMask: boolean;
};

export type TBroadcastAwayModeDisabled = {
	broadcastStatus: EBroadcastStatus;
	withClientAudio: boolean;
	withClientAudioRequest: boolean;
};

export type TPrivateRequestRefusedByModel = {
	clientChatId: string;
	privateRequestId: number;
	userId: string;
};

export type TPrivateRequestRefusedByBilling = {
	privateRequestId: number;
	userId: string;
	clientChatId: string;
	clientNickname: string;
};

export type TBroadcastPrivateModeEnabledForModel = {
	balance: number;
	clientChatId: string;
	currentTimestamp: number;
	id: number;
	modelWithClientAudio: boolean;
	outerPrivate: boolean;
	privateModeStartedAtTimestamp: number;
	requestWithClientAudio: boolean;
	roomChatId: string;
	userId: string;
	userNickname: string;
};

export type TSoundNotificationTriggered = {
	eventType: string;
	amount: number;
};

export type TUser = {
	balance: number;
	categoryDescription: string;
	categoryTitle: string;
	color: string;
	displayName: string;
	id: string;
	isUnique: boolean;
	isWhale: boolean;
	hasToys: boolean;
	postfix: string;
	userId: string;
};

export type TRoomChatUsersUpdate = {
	guestsCount: number;
	totalCount: number;
	users: TUser[];
};

export type TPrivateRequestCreatedByClient = {
	balance: number;
	isWhale: boolean;
	privateRequestId: number;
	remark: null;
	tipperCategory: number;
	tipperCategoryDescription: string;
	tipperColor: string;
	userId: string;
	withClientAudio: boolean;
	nickname: string;
	participant?: string;
};

export type TMessageResponse = {
	type: string;
	timestamp: number;
	correlationId: string;
	toClientId: string;
	from: {
		type: string;
		id: string;
		nickname: string;
		participant: string;
	};
	fromUserId: string;
	fromClientNickname: string;
	payload: {
		message: string;
		userType: string;
		isWhale: boolean;
		tipperCategory: number;
		tipperColor: string;
		tipperCategoryDescription: string;
		remark: string | null;
		bof: boolean;
		eof: boolean;
		amount: number;
		title: string;
		description: string;
		id: string;
		duration: string;
		vibration: string;
		isPrivate: boolean;
		price: number;
		remainingMinutes?: number;
		remainingSeconds?: number;
	};
	toRoomChatId: string;
	isModel: boolean;
};

export type TBroadcastGoalStartedResponse = {
	type: string;
	timestamp: number;
	correlationId: string;
	payload: {
		bof: boolean;
		eof: boolean;
		goal: {
			amount: number;
			id: string;
			title: string;
			total: number;
		};
	};
	toRoomChatId: string;
	isModel: boolean;
};

export type TBroadcastGoalEndedResponse = {
	type: string;
	timestamp: number;
	correlationId: string;
	payload: {
		bof?: boolean;
		eof?: boolean;
		goal: {
			amount: number;
			id: string;
			title: string;
			total: number;
		};
		reason: string;
	};
	toRoomChatId: string;
	isModel: boolean;
};

export type TToyControlSessionChangedResponse = {
	type: string;
	timestamp: number;
	correlationId: string;
	payload: {
		broadcastId: string;
		privateRequestId: number;
		cause: ECause;
		notice: string;
		at: string;
	};
	toClientId: string;
};

export enum ECause {
	CREATED = "session.created",
	CLOSED = "session.closed",
	OFFERED = "consent.offered",
	REQUESTED = "consent.requested",
	ACCEPTED = "consent.accepted",
	DECLINED = "consent.declined",
	CANCELED = "consent.canceled",
	REVOKED = "consent.revoked",
	EXPIRED = "consent.expired",
	ONLINE = "device.online",
	OFFLINE = "device.offline",
	STARTED = "motion.started",
	STOPPED = "motion.stopped",
}
